import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import {
  CreateRestaurant,
  Restaurant,
  RestaurantWithTables,
  TableAvailability,
} from '../_models/Restaurant';
import {
  CreateRestaurantSlotRequestBody,
  DuplicateSlotRequestBody,
  DuplicateSlotsRequestBody,
  RestaurantSlot,
  RestaurantSlotTable,
  RestaurantSlotTableWithInfo,
  RestaurantSlotWithTables,
} from '../_models/RestaurantSlot';
import { Passenger } from '../_models/Passenger';
import {
  PassengerReservation,
  Reservation,
  ReservationResponse,
} from '../_models/Reservation';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  Restaurants() {
    // Initialize Params Object
    let params = new HttpParams();

    // Begin assigning parameters
    params = params.append('active', true);

    let url = `${environment.host}/restaurants`;
    return this.http.get<Restaurant[]>(url, { params: params });
  }

  Restaurant(id: string) {
    let url = `${environment.host}/restaurants/${id}`;
    return this.http.get<RestaurantWithTables>(url);
  }

  CreateRestaurant(restaurant: CreateRestaurant) {
    let url = `${environment.host}/restaurants`;
    return this.http.post<CreateRestaurant>(url, restaurant);
  }

  UpdateRestaurant(id_restaurant: string, restaurant: CreateRestaurant) {
    let url = `${environment.host}/restaurants/${id_restaurant}`;
    return this.http.put<RestaurantWithTables>(url, restaurant);
  }

  Slots(id_restaurant: string, date: string) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params.append('date', date);
    let url = `${environment.host}/restaurants/${id_restaurant}/slots`;
    return this.http.get<RestaurantSlot[]>(url, { params: params });
  }

  Slot(id_restaurant: string, id_slot: string) {
    let url = `${environment.host}/restaurants/${id_restaurant}/slots/${id_slot}`;
    return this.http.get<RestaurantSlotWithTables>(url);
  }

  DeleteSlot(id_restaurant: string, id_slot: string) {
    let url = `${environment.host}/restaurants/${id_restaurant}/slots/${id_slot}`;
    return this.http.delete(url);
  }
  Passenger(id_passenger: string) {
    let url = `${environment.host}/passengers/${id_passenger}`;
    return this.http.get<Passenger>(url);
  }

  Passengers(id_cabin: string, id_ship: string) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params.append('id_cabin', id_cabin).append('id_ship', id_ship);
    let url = `${environment.host}/passengers`;
    return this.http.get<Passenger[]>(url, { params: params });
  }

  PassengerReservations(id_passenger: string) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params.append('active', true);
    let url = `${environment.host}/passengers/${id_passenger}/reservations`;
    return this.http.get<PassengerReservation[]>(url, { params: params });
  }

  PassengerCompanions(id_passenger: string, date: string) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params.append('date', date);
    let url = `${environment.host}/passengers/${id_passenger}/companions`;
    return this.http.get<Passenger[]>(url, { params: params });
  }
  ReservationCompanions(id_passenger: string, id_reservation: string) {
    let url = `${environment.host}/passengers/${id_passenger}/companions/${id_reservation}`;
    return this.http.get<Passenger[]>(url);
  }

  TableSlots(
    id_restaurant: string,
    date: string,
    id_restaurant_slot_table?: number
  ) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params.append('date', date).append('booked', false);
    if (id_restaurant_slot_table) {
      params = params.append('include', id_restaurant_slot_table);
    }
    let url = `${environment.host}/restaurants/${id_restaurant}/tables`;
    return this.http.get<RestaurantSlotTableWithInfo[]>(url, {
      params: params,
    });
  }

  CreateReservation(reservation: Reservation) {
    let url = `${environment.host}/reservations`;
    return this.http.post(url, reservation);
  }

  DeleteReservation(id_reservation: string) {
    let url = `${environment.host}/reservations/${id_reservation}`;
    return this.http.delete(url);
  }

  UpdateReservation(id_reservation: string, reservation: Reservation) {
    let url = `${environment.host}/reservations/${id_reservation}`;
    return this.http.put(url, reservation);
  }

  CreateRestaurantSlot(
    id_restaurant: string,
    slot: CreateRestaurantSlotRequestBody
  ) {
    let url = `${environment.host}/restaurants/${id_restaurant}/slots`;
    return this.http.post(url, slot);
  }

  UpdateRestaurantSlot(
    id_restaurant: string,
    id_slot: string,
    slot: CreateRestaurantSlotRequestBody
  ) {
    let url = `${environment.host}/restaurants/${id_restaurant}/slots/${id_slot}`;
    return this.http.put(url, slot);
  }

  DuplicateSlot(
    id_restaurant: string,
    id_slot: string,
    payload: DuplicateSlotRequestBody
  ) {
    let url = `${environment.host}/restaurants/${id_restaurant}/slots/${id_slot}/duplicate`;
    return this.http.post(url, payload);
  }
  DuplicateSlots(id_restaurant: string, payload: DuplicateSlotsRequestBody) {
    let url = `${environment.host}/restaurants/${id_restaurant}/slots/duplicate`;
    return this.http.post(url, payload);
  }

  Reservations(
    uuid_restaurant: string,
    date_from?: string,
    date_to?: string,
    exploded: boolean = false
  ) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params
      .append('uuid_restaurant', uuid_restaurant)
      .append('exploded', exploded);
    if (date_from) {
      params = params.append('date_from', date_from);
    }
    if (date_to) {
      params = params.append('date_to', date_to);
    }
    let url = `${environment.host}/reservations`;
    return this.http.get<ReservationResponse[]>(url, { params: params });
  }

  Cabins(id_ship: string) {
    let url = `${environment.host}/ships/${id_ship}/cabins`;
    return this.http.get<string[]>(url);
  }

  TableAvailability(
    id_restaurant: string,
    date: string,
    start_time: string,
    end_time: string,
    id_restaurant_slot?: string
  ) {
    let params = new HttpParams();

    // Begin assigning parameters
    params = params
      .append('date', date)
      .append('start_time', start_time)
      .append('end_time', end_time);
    if (id_restaurant_slot) {
      params = params.append('id_restaurant_slot', id_restaurant_slot);
    }

    let url = `${environment.host}/restaurants/${id_restaurant}/tables/availability`;
    return this.http.get<TableAvailability[]>(url, { params: params });
  }
}
